import React, { FunctionComponent, useCallback, useState } from 'react'
import { browserHistory } from 'react-router'
import { useMfaStatus } from './graphql/status'
import { disableSms } from './graphql/sms'
import { disableTotp } from './graphql/totp'
import ConfigureSmsApp from './ConfigureSmsApp'
import ConfigureTotpApp from './ConfigureTotpApp'
import MfaMenu from './components/MfaMenu'
import DisableStep from './components/DisableStep'
import { Dialog, toaster } from 'evergreen-ui'
import { userSettingsPath } from 'lib/path-helpers/user'

export enum MfaOptionTypes {
  sms = 'sms',
  disableSms = 'disableSms',
  totp = 'totp',
  disableTotp = 'disableTotp',
  none = ''
}

interface MfaAppProps {
  onClose?: (isMfaSetup: boolean) => void
}

const MfaApp: FunctionComponent<MfaAppProps> = props => {
  const { onClose } = props

  const [mfaOptionSelected, setMfaOptionSelected] = useState(MfaOptionTypes.none)
  const [password, setPassword] = useState('')

  const {
    isLoading,
    isErrored,
    isEnabled,
    isSmsEnabled,
    isTotpEnabled,
    lastFourPhone,
    refetchMfaStatus
  } = useMfaStatus()

  const handleCloseComplete = useCallback(() => {
    // A close callback can be set for when the MFA dialog is mounted in another page (e.g. /settings/user/mfa/check)
    // When it's mounted as a standalone route, closing the dialog redirects to the User Settings page (e.g. /settings/user/mfa)
    if (onClose) {
      onClose(isEnabled)
    } else {
      browserHistory.push(userSettingsPath())
    }
  }, [onClose, isEnabled])

  const handleDisableBack = useCallback(() => {
    setMfaOptionSelected(MfaOptionTypes.none)
    setPassword('')
  }, [])

  const onDisableSms = useCallback(async () => {
    try {
      const disableRes = await disableSms(password)

      const hasError = Boolean(disableRes && disableRes.errors)
      if (hasError) {
        throw new Error('Error disabling SMS MFA')
      } else {
        toaster.success(`You have successfully disabled Text Message Multi-Factor Authentication`)
      }
    } catch (e) {
      toaster.danger(`Something went wrong trying to disable your Text Message Multi-Factor Authentication`)
    }

    handleDisableBack()
  }, [password, handleDisableBack])

  const onDisableTotp = useCallback(async () => {
    try {
      const disableRes = await disableTotp(password)

      const hasError = Boolean(disableRes && disableRes.errors)
      if (hasError) {
        throw new Error('Error disabling SMS MFA')
      } else {
        toaster.success(`You have successfully disabled Authenticator App Multi-Factor Authentication`)
      }
    } catch (e) {
      toaster.danger(`Something went wrong trying to disable your Authenticator App Multi-Factor Authentication`)
    }

    handleDisableBack()
  }, [password, handleDisableBack])

  return (
    <Dialog
      title="Multi-Factor Authentication"
      isShown
      hasFooter={false}
      onCloseComplete={handleCloseComplete}
      contentContainerProps={{ padding: 0 }}
    >
      {mfaOptionSelected === MfaOptionTypes.sms && (
        <ConfigureSmsApp configureTfaMethod={setMfaOptionSelected} refetchMfaStatus={refetchMfaStatus} />
      )}
      {mfaOptionSelected === MfaOptionTypes.disableSms && (
        <DisableStep
          onBack={handleDisableBack}
          onDisable={onDisableSms}
          onPasswordChange={setPassword}
          password={password}
          mfaName="Text Message"
        />
      )}

      {mfaOptionSelected === MfaOptionTypes.totp && (
        <ConfigureTotpApp configureTfaMethod={setMfaOptionSelected} refetchMfaStatus={refetchMfaStatus} />
      )}
      {mfaOptionSelected === MfaOptionTypes.disableTotp && (
        <DisableStep
          onBack={handleDisableBack}
          onDisable={onDisableTotp}
          onPasswordChange={setPassword}
          password={password}
          mfaName="Authenticator App"
        />
      )}

      {mfaOptionSelected === MfaOptionTypes.none && (
        <MfaMenu
          isLoading={isLoading}
          isErrored={isErrored}
          configureTfaMethod={setMfaOptionSelected}
          isSmsEnabled={isSmsEnabled}
          isTotpEnabled={isTotpEnabled}
          lastFourPhone={lastFourPhone}
        />
      )}
    </Dialog>
  )
}

export default MfaApp
